import { type Op } from 'quill-delta';

import { type ADFDocument } from '@canvas/adf-types';

import { type AiActor, type FieldType } from './field';

export type ValueJSON<T extends FieldType, U> = {
  type: T;
  value: U;
};

export interface SingleTextValue {
  plain: string;
  rich?: ADFDocument;
}

export interface AiValueMetadata {
  lastUpdatedAt?: string;
  lastUpdatedBy?: AiActor;
  lastUpdatedUserAccountId?: string;
}

export interface AiTextValue extends SingleTextValue {
  metadata?: AiValueMetadata;
}

export interface TextValue {
  get type(): FieldType;
  toJSON(): ValueJSON<FieldType.Text, SingleTextValue>;
  get value(): SingleTextValue;
}

export interface AiSummaryValue {
  get type(): FieldType;
  toJSON(): ValueJSON<FieldType.AiSummary, AiTextValue>;
  get value(): AiTextValue;
}

export interface MutableTextValue {
  insert(index: number, content: string): void;
  delete(index: number, length: number): void;
  applyDelta(delta: Op[]): void;
  setContent(adf: ADFDocument): void;
  setRichContent(adf: ADFDocument): void;
}

export interface MutableAiSummaryValue extends AiSummaryValue, MutableTextValue {
  markUserUpdated(accountId: string | undefined, date?: Date): void;
}

export interface NumberValue {
  get type(): FieldType;
  toJSON(): ValueJSON<FieldType.Number, string>;
  get value(): string;
}

export interface MutableNumberValue extends NumberValue {
  set value(val: string);
}

export interface DateValue {
  get type(): FieldType;
  toJSON(): ValueJSON<FieldType.Date, string>;
  get value(): string;
}

export interface MutableDateValue extends DateValue {
  set value(val: string);
}

export interface CheckboxValue {
  get type(): FieldType;
  toJSON(): ValueJSON<FieldType.Checkbox, boolean>;
  get value(): boolean;
}

export interface MutableCheckboxValue extends CheckboxValue {
  set value(val: boolean);
}

export interface SelectValue {
  get type(): FieldType;

  /**
   * The list of selected select option IDs, in any order (the UI should render these in the order that
   * is defined in the field settings.
   */
  get value(): string[];

  toJSON(): ValueJSON<FieldType.Select, string[]>;
}

export interface MutableSelectValue extends SelectValue {
  setTo(optionId: string[]): void;
  add(optionId: string): void;
  delete(optionIds: string[]): void;
}

export interface SingleAttachmentValue {
  fileId: string;
  id: string;
}

export interface AttachmentValue {
  get type(): FieldType;
  get value(): SingleAttachmentValue[];
  toJSON(): ValueJSON<FieldType.Attachment, SingleAttachmentValue[]>;
}

export interface MutableAttachmentValue {
  add(value: SingleAttachmentValue): void;
  delete(index: number): void;
  reorder(index: number, newIndex: number): void;
  setTo(value: SingleAttachmentValue[]): void;
}

export interface SinglePageLinkValue {
  contentId: string | undefined;
  ari: string | undefined;
  id: string;
}

export interface PageLinkValue {
  get type(): FieldType;
  get value(): SinglePageLinkValue[];
  toJSON(): ValueJSON<FieldType.PageLink, SinglePageLinkValue[]>;
}

export interface MutablePageLinkValue {
  add(value: SinglePageLinkValue): void;
  delete(index: number): void;
  reorder(index: number, newIndex: number): void;
  setTo(value: SinglePageLinkValue[]): void;
}

export enum UserType {
  Referenced,
  Current
}

export interface ReferencedUserValue {
  accountId?: string;
  id: string;
  type: UserType.Referenced;
}

export interface CurrentUserValue {
  type: UserType.Current;
}

export type SingleUserValue = ReferencedUserValue | CurrentUserValue;

export interface UserValue {
  get type(): FieldType;
  get value(): SingleUserValue[];

  toJSON(): ValueJSON<FieldType.User, SingleUserValue[]>;
}

export interface MutableUserValue {
  add(value: SingleUserValue): void;
  insert(index: number, value: SingleUserValue): void;
  delete(index: number): void;
  reorder(index: number, newIndex: number): void;
  setTo(value: SingleUserValue[]): void;
}

export interface SingleHyperlinkValue {
  url: string;
  id: string;
}

export interface MutableSingleHyperlinkValue extends SingleHyperlinkValue {
  applyUrlDelta(delta: Op[]): void;
}

export interface HyperlinkValue {
  get type(): FieldType;
  get value(): SingleHyperlinkValue[];
  toJSON(): ValueJSON<FieldType.Hyperlink, SingleHyperlinkValue[]>;
}

export interface MutableHyperlinkValue extends HyperlinkValue {
  get value(): MutableSingleHyperlinkValue[];
  add(link: SingleHyperlinkValue): void;
  delete(index: number): void;
  reorder(index: number, newIndex: number): void;
  setTo(value: SingleHyperlinkValue[]): void;
}

export interface SingleEntryLinkValue {
  entryId: string;
  id: string;
}

export interface EntryLinkValue {
  get type(): FieldType;
  get value(): SingleEntryLinkValue[];
  toJSON(): ValueJSON<FieldType.EntryLink, SingleEntryLinkValue[]>;
}

export interface MutableEntryLinkValue extends EntryLinkValue {
  add(value: SingleEntryLinkValue): void;
  delete(index: number): void;
  reorder(index: number, newIndex: number): void;
  setTo(value: SingleEntryLinkValue[]): void;
}

export interface SingleJiraIssueValue {
  // key here refers to Jira issue key
  key: string;
  id: string;
}

export interface JiraIssueValue {
  get type(): FieldType;
  get value(): SingleJiraIssueValue[];
  toJSON(): ValueJSON<FieldType.JiraIssue, SingleJiraIssueValue[]>;
}

export interface MutableJiraIssueValue extends JiraIssueValue {
  add(value: SingleJiraIssueValue): void;
  delete(index: number): void;
  reorder(index: number, newIndex: number): void;
  setTo(value: SingleJiraIssueValue[]): void;
}

export type Value<T extends FieldType> = {
  [FieldType.Text]: TextValue;
  [FieldType.AiSummary]: AiSummaryValue;
  [FieldType.Date]: DateValue;
  [FieldType.Number]: NumberValue;
  [FieldType.Select]: SelectValue;
  [FieldType.User]: UserValue;
  [FieldType.Attachment]: AttachmentValue;
  [FieldType.Checkbox]: CheckboxValue;
  [FieldType.JiraIssue]: JiraIssueValue;
  [FieldType.PageLink]: PageLinkValue;
  [FieldType.Hyperlink]: HyperlinkValue;
  [FieldType.EntryLink]: EntryLinkValue;

  [FieldType.Backlink]: never;
  [FieldType.EntryDetails]: never;
  [FieldType.JiraIssueDetails]: never;
  [FieldType.PageDetails]: never;
  [FieldType.PageLabels]: never;
  [FieldType.PageStatus]: never;
}[T] & { isEmpty: boolean };

export type MutableValue<T extends FieldType> = {
  [FieldType.Text]: TextValue & MutableTextValue;
  [FieldType.AiSummary]: MutableAiSummaryValue;
  [FieldType.Date]: DateValue & MutableDateValue;
  [FieldType.Number]: NumberValue & MutableNumberValue;
  [FieldType.Select]: SelectValue & MutableSelectValue;
  [FieldType.User]: UserValue & MutableUserValue;
  [FieldType.Attachment]: AttachmentValue & MutableAttachmentValue;
  [FieldType.Checkbox]: CheckboxValue & MutableCheckboxValue;
  [FieldType.JiraIssue]: JiraIssueValue & MutableJiraIssueValue;
  [FieldType.PageLink]: PageLinkValue & MutablePageLinkValue;
  [FieldType.Hyperlink]: HyperlinkValue & MutableHyperlinkValue;
  [FieldType.EntryLink]: EntryLinkValue & MutableEntryLinkValue;

  [FieldType.EntryDetails]: never;
  [FieldType.Backlink]: never;
  [FieldType.JiraIssueDetails]: never;
  [FieldType.PageLabels]: never;
  [FieldType.PageStatus]: never;
  [FieldType.PageDetails]: never;
}[T] & { isEmpty: boolean };

export type AllValueJSON =
  | ValueJSON<FieldType.Text, SingleTextValue>
  | ValueJSON<FieldType.AiSummary, AiTextValue>
  | ValueJSON<FieldType.Number, string>
  | ValueJSON<FieldType.Date, string>
  | ValueJSON<FieldType.Select, string[]>
  | ValueJSON<FieldType.User, SingleUserValue[]>
  | ValueJSON<FieldType.Attachment, SingleAttachmentValue[]>
  | ValueJSON<FieldType.Checkbox, boolean>
  | ValueJSON<FieldType.PageLink, SinglePageLinkValue[]>
  | ValueJSON<FieldType.Hyperlink, SingleHyperlinkValue[]>
  | ValueJSON<FieldType.EntryLink, SingleEntryLinkValue[]>
  | ValueJSON<FieldType.JiraIssue, SingleJiraIssueValue[]>;
